import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, ZoomControl } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import MapResetButton from 'src/shared/components/map-reset-button/MapResetButton';
import config from 'src/config';
import ReactLeafletCustomControl from 'src/shared/components/react-leaflet-custom-control/ReactLeafletCustomControl';
import googleMapStyles from 'src/shared/data/googleMapStyles';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import ExploreButton from 'src/shared/components/explore-button/ExploreButton';
import useProject from 'src/shared/hooks/useProject';
import useSiteSummaryAnalytics from 'src/app/projects/views/site-summary/hooks/useSiteSummaryAnalytics';
import { useAppSelector } from 'src/store';
import { getMapRoutesAvailability } from 'src/routes/state/routesSelector';
import styles from './SpeciesMiniMap.module.scss';
import SpeciesMiniMapMarkers from './SpeciesMiniMapMarkers';
import { useSpeciesMapQuery } from '../../state/api/speciesMapGraphSlice';
import 'leaflet/dist/leaflet.css';
import 'src/shared/styles/leaflet.css';

const SpeciesMiniMap = () => {
    const GoogleLayer = ReactLeafletGoogleLayer as any;
    const mapRef = useRef<L.Map>(null);
    const navigate = useAppNavigation();
    const { trackExploreButtonClick } = useSiteSummaryAnalytics();

    const { currentProjectId } = useProject();

    // The species map route is permission-gated, so only offer the link when the
    // user can actually get there — otherwise ProtectedRoute bounces them to /projects.
    const { speciesMap: isSpeciesMapAvailable } = useAppSelector(getMapRoutesAvailability);

    const { currentData } = useSpeciesMapQuery(
        {
            projectId: currentProjectId || '',
            samples: {},
        },
        { skip: !currentProjectId }
    );

    const bounds = useMemo(() => {
        const samples = currentData?.speciesMapData.samples || [];
        const coords: [number, number][] = samples
            .filter(s => Array.isArray(s.location?.coordinates) && s.location.coordinates.length >= 2)
            .map(s => [Number(s.location.coordinates[0]), Number(s.location.coordinates[1])]);
        if (!coords.length) return undefined;
        return L.latLngBounds(coords as any);
    }, [currentData]);

    useEffect(() => {
        if (bounds && mapRef.current) {
            mapRef.current.fitBounds(bounds);
        }
    }, [bounds]);

    return (
        <div className={styles.container}>
            <MapContainer
                ref={mapRef}
                bounds={bounds}
                scrollWheelZoom={false}
                zoomControl={false}
                maxZoom={19}
                zoom={0}
                attributionControl={false}
                style={{ width: '100%', height: '100%' }}
            >
                <GoogleLayer
                    apiKey={config.googleMapsJavascriptApiKey}
                    useGoogMapsLoader={true}
                    type={'roadmap' as const}
                    styles={googleMapStyles as any}
                />

                {currentData?.speciesMapData?.samples && <SpeciesMiniMapMarkers samples={currentData.speciesMapData.samples} />}

                {isSpeciesMapAvailable && (
                    <div className={styles.exploreBtnContainer}>
                        <ExploreButton
                            buttonText='Open species map'
                            onClick={() => {
                                trackExploreButtonClick({ section: 'species_map' });
                                navigate.toSpeciesMap();
                            }}
                            className={styles.exploreBtn}
                        />
                    </div>
                )}

                <ZoomControl position='bottomright' />
                <ReactLeafletCustomControl position='bottomright'>
                    <MapResetButton onClick={() => (bounds ? mapRef.current?.fitBounds(bounds) : null)} />
                </ReactLeafletCustomControl>
            </MapContainer>
        </div>
    );
};

export default SpeciesMiniMap;
